import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../../services/supabase';
import { PageHeader, Card, CardHeader, Table, Td, EmptyRow, Btn, Badge, Modal, FormField, Input, Select, Textarea, useToast, FeatureLocked } from '../../components/ui';

// ── LEAVE REQUESTS ────────────────────────────────────────────────────────
export function CompanyLeaves({ companyId, featureEnabled }: { companyId: string; featureEnabled: boolean }) {
  const toast = useToast();
  const [leaves, setLeaves] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [rejectModal, setRejectModal] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const load = useCallback(async () => {
    const [{ data: lv }, { data: lt }] = await Promise.all([
      sb.from('leaves').select('*, user:users(full_name, employee_code), leave_type:leave_types(name)').eq('company_id', companyId).order('created_at', { ascending: false }),
      sb.from('leave_types').select('*').eq('company_id', companyId),
    ]);
    setLeaves(lv || []);
    setLeaveTypes(lt || []);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (!featureEnabled) return <FeatureLocked feature="Leave Management" />;

  const updateStatus = async (id: string, status: 'approved' | 'rejected', reason?: string) => {
    const { data: { user } } = await sb.auth.getUser();
    const leave = leaves.find(l => l.id === id);
    const { error } = await sb.from('leaves').update({
      status, approved_by: user?.id, approved_at: new Date().toISOString(),
      rejection_reason: reason || null,
    }).eq('id', id);
    if (error) { toast(error.message, 'error'); return; }

    // Send push notification
    try {
      const { data: { session } } = await sb.auth.getSession();
      if (session && leave) {
        await fetch('https://quxasqjlhohghnuurvvl.supabase.co/functions/v1/send-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ type: status === 'approved' ? 'leave_approved' : 'leave_rejected', payload: { employee_id: leave.user_id, company_id: companyId, start_date: leave.start_date, end_date: leave.end_date } }),
        });
      }
    } catch {}

    toast(status === 'approved' ? '✅ Leave approved!' : '❌ Leave rejected', status === 'approved' ? 'success' : 'error');
    setRejectModal(null); setRejectionReason('');
    load();
  };

  const filtered = filter === 'all' ? leaves : leaves.filter(l => l.status === filter);
  const pending = leaves.filter(l => l.status === 'pending').length;

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Leave Requests" subtitle={`${pending} pending approval`} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['pending', 'approved', 'rejected', 'all'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: filter === f ? 'var(--accent)' : 'transparent', color: filter === f ? '#000' : 'var(--muted)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'capitalize' }}>
            {f} {f === 'pending' && pending > 0 && <span style={{ background: '#fff3', borderRadius: 10, padding: '1px 6px', fontSize: 11, marginLeft: 4 }}>{pending}</span>}
          </button>
        ))}
      </div>
      <Card>
        <Table headers={['Employee', 'Type', 'From', 'To', 'Days', 'Reason', 'Status', 'Actions']}>
          {filtered.length === 0 ? <EmptyRow cols={8} message={`No ${filter === 'all' ? '' : filter} leave requests`} /> :
           filtered.map(l => {
            const days = l.total_days || Math.ceil((new Date(l.end_date).getTime() - new Date(l.start_date).getTime()) / 86400000) + 1;
            return (
              <tr key={l.id}>
                <Td><div style={{ fontWeight: 600 }}>{l.user?.full_name}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>{l.user?.employee_code}</div></Td>
                <Td><Badge type="purple">{l.leave_type?.name || 'Leave'}</Badge></Td>
                <Td mono>{l.start_date}</Td>
                <Td mono>{l.end_date}</Td>
                <Td mono>{days}d{l.half_day ? ' (Half)' : ''}</Td>
                <Td><span style={{ fontSize: 12, color: 'var(--muted)', maxWidth: 180, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.reason || '—'}</span></Td>
                <Td><Badge type={l.status === 'approved' ? 'green' : l.status === 'rejected' ? 'red' : 'yellow'}>{l.status}</Badge>{l.rejection_reason && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 2 }}>{l.rejection_reason}</div>}</Td>
                <Td>
                  {l.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn size="sm" onClick={() => updateStatus(l.id, 'approved')}>✅ Approve</Btn>
                      <Btn variant="danger" size="sm" onClick={() => { setRejectModal(l); setRejectionReason(''); }}>✗ Reject</Btn>
                    </div>
                  ) : <span style={{ fontSize: 12, color: 'var(--muted)' }}>{l.approved_at ? new Date(l.approved_at).toLocaleDateString('en-IN') : '—'}</span>}
                </Td>
              </tr>
            );
          })}
        </Table>
      </Card>

      {rejectModal && (
        <Modal title={`Reject Leave — ${rejectModal.user?.full_name}`} onClose={() => setRejectModal(null)}>
          <FormField label="Reason for rejection (optional)">
            <Textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Please provide a reason..." />
          </FormField>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setRejectModal(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={() => updateStatus(rejectModal.id, 'rejected', rejectionReason)}>Reject Leave</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── LEAVE SETUP ───────────────────────────────────────────────────────────
export function LeaveSetup({ companyId, featureEnabled }: { companyId: string; featureEnabled: boolean }) {
  const toast = useToast();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);
  const [policy, setPolicy] = useState<any>(null);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', days_allowed: '12', carry_forward: 'false', paid: 'true', requires_approval: 'true', applicable_after_days: '0' });
  const [policyForm, setPolicyForm] = useState({ probation_days: '90', min_advance_notice_days: '1', max_consecutive_days: '10', weekend_counts_as_leave: 'false' });

  const load = useCallback(async () => {
    const [{ data: lt }, { data: pol }] = await Promise.all([
      sb.from('leave_types').select('*').eq('company_id', companyId).order('created_at'),
      sb.from('leave_policies').select('*').eq('company_id', companyId).single(),
    ]);
    setLeaveTypes(lt || []);
    if (pol) {
      setPolicy(pol);
      setPolicyForm({ probation_days: String(pol.probation_days), min_advance_notice_days: String(pol.min_advance_notice_days), max_consecutive_days: String(pol.max_consecutive_days), weekend_counts_as_leave: String(pol.weekend_counts_as_leave) });
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (!featureEnabled) return <FeatureLocked feature="Leave Management" />;

  const saveType = async () => {
    setSaving(true);
    const { error } = await sb.from('leave_types').insert({ company_id: companyId, name: form.name, days_allowed: parseInt(form.days_allowed), carry_forward: form.carry_forward === 'true', paid: form.paid === 'true', requires_approval: form.requires_approval === 'true', applicable_after_days: parseInt(form.applicable_after_days) });
    setSaving(false);
    if (error) { toast(error.message, 'error'); return; }
    toast('Leave type created!', 'success'); setModal(false); load();
  };

  const delType = async (id: string) => {
    await sb.from('leave_types').delete().eq('id', id); load();
  };

  const savePolicy = async () => {
    setSaving(true);
    const data = { company_id: companyId, probation_days: parseInt(policyForm.probation_days), min_advance_notice_days: parseInt(policyForm.min_advance_notice_days), max_consecutive_days: parseInt(policyForm.max_consecutive_days), weekend_counts_as_leave: policyForm.weekend_counts_as_leave === 'true' };
    if (policy) await sb.from('leave_policies').update(data).eq('id', policy.id);
    else await sb.from('leave_policies').insert(data);
    setSaving(false); toast('Policy saved!', 'success'); load();
  };

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Leave Policy & Types" subtitle="Configure leave types and company leave policy" />

      {/* Leave Types */}
      <Card>
        <CardHeader title="Leave Types" action={<Btn size="sm" onClick={() => setModal(true)}>+ Add Type</Btn>} />
        <Table headers={['Type', 'Days/Year', 'Carry Forward', 'Paid', 'Advance Notice', 'Actions']}>
          {leaveTypes.length === 0 ? <EmptyRow cols={6} message="No leave types. Add types to enable leave requests." /> :
           leaveTypes.map(lt => (
            <tr key={lt.id}>
              <Td><strong>{lt.name}</strong></Td>
              <Td mono>{lt.days_allowed === 0 ? 'Unlimited (LOP)' : `${lt.days_allowed} days`}</Td>
              <Td><Badge type={lt.carry_forward ? 'green' : 'red'}>{lt.carry_forward ? 'Yes' : 'No'}</Badge></Td>
              <Td><Badge type={lt.paid ? 'green' : 'yellow'}>{lt.paid ? 'Paid' : 'Unpaid'}</Badge></Td>
              <Td mono>{lt.applicable_after_days > 0 ? `After ${lt.applicable_after_days} days` : 'Immediate'}</Td>
              <Td><Btn variant="danger" size="sm" onClick={() => delType(lt.id)}>Delete</Btn></Td>
            </tr>
          ))}
        </Table>
      </Card>

      {/* Leave Policy */}
      <Card>
        <CardHeader title="Leave Policy" />
        <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            ['Probation Period (days)', 'probation_days', 'number'],
            ['Min Advance Notice (days)', 'min_advance_notice_days', 'number'],
            ['Max Consecutive Days', 'max_consecutive_days', 'number'],
          ].map(([label, key, type]) => (
            <FormField key={key} label={label}>
              <Input type={type as any} value={(policyForm as any)[key]} onChange={e => setPolicyForm(f => ({...f, [key]: e.target.value}))} />
            </FormField>
          ))}
          <FormField label="Weekend Counts as Leave Day">
            <Select value={policyForm.weekend_counts_as_leave} onChange={e => setPolicyForm(f => ({...f, weekend_counts_as_leave: e.target.value}))}>
              <option value="false">No (Weekend excluded)</option>
              <option value="true">Yes (Weekend counted)</option>
            </Select>
          </FormField>
          <div style={{ gridColumn: '1/-1' }}>
            <Btn onClick={savePolicy} disabled={saving}>{saving ? 'Saving...' : 'Save Policy'}</Btn>
          </div>
        </div>
      </Card>

      {modal && (
        <Modal title="Add Leave Type" onClose={() => setModal(false)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}><FormField label="Leave Type Name"><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Casual Leave, Sick Leave..." /></FormField></div>
            <FormField label="Days Allowed Per Year"><Input type="number" value={form.days_allowed} onChange={e => setForm(f => ({...f, days_allowed: e.target.value}))} placeholder="0 = unlimited (LOP)" /></FormField>
            <FormField label="Applicable After (days)"><Input type="number" value={form.applicable_after_days} onChange={e => setForm(f => ({...f, applicable_after_days: e.target.value}))} placeholder="0 = immediate" /></FormField>
            <FormField label="Carry Forward">
              <Select value={form.carry_forward} onChange={e => setForm(f => ({...f, carry_forward: e.target.value}))}>
                <option value="false">No</option><option value="true">Yes</option>
              </Select>
            </FormField>
            <FormField label="Leave Type">
              <Select value={form.paid} onChange={e => setForm(f => ({...f, paid: e.target.value}))}>
                <option value="true">Paid</option><option value="false">Unpaid (LOP)</option>
              </Select>
            </FormField>
            <FormField label="Requires Approval">
              <Select value={form.requires_approval} onChange={e => setForm(f => ({...f, requires_approval: e.target.value}))}>
                <option value="true">Yes</option><option value="false">Auto-approved</option>
              </Select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn onClick={saveType} disabled={saving}>{saving ? 'Saving...' : 'Create'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
